// File: MessagesScreen.js
//
// The Messages page. Where users chat back-and-forth with the H.O.P.E
// team in threads (kind of like a simple support inbox).
//
// What you see here depends on who's logged in:
//   - Regular users see only the threads they're a participant in.
//   - Staff/admins see every open and closed thread, and can reply,
//     close a thread, or invite another admin into a thread.
//
// Layout: two columns. Left = list of threads (Ongoing + Closed).
// Right = the active thread's messages + a box to type a reply.
// On phones the columns toggle (you see one or the other at a time).
//
// Sections in this file:
//   - Helper functions for parsing message text (bullet lists, etc.)
//   - FormattedMessageText (renders one message's text with formatting)
//   - MessagesScreen (the actual page: state, loading data, rendering)

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "./firebaseConfig";
import { signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  documentId,
  getDoc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  limit,
  getDocs,
  arrayUnion,
} from "firebase/firestore";

import logo from "./assets/512.png";
import { COLORS, THEME, pageBackground } from "./theme";
import ThreadRow from "./components/ThreadRow";
import LogoutConfirm from "./components/LogoutConfirm";

const db = getFirestore();

// --- Message text formatting helpers ---
// Plain-text messages can include bullet/numbered lists. The helpers
// below detect those line styles so FormattedMessageText (further
// down) can render them as real <ul> / <ol> lists.
function isBulletLine(line) {
  return /^(-|\*|•)\s+/.test(line);
}

function isNumberedLine(line) {
  return /^\d+[.)]\s+/.test(line);
}

function stripListMarker(line) {
  return line
    .replace(/^(-|\*|•)\s+/, "")
    .replace(/^\d+[.)]\s+/, "")
    .trim();
}

function parseMessageBlocks(text) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) {
      i += 1;
      continue;
    }

    if (isBulletLine(line)) {
      const items = [];
      while (i < lines.length && isBulletLine(lines[i].trim())) {
        items.push(stripListMarker(lines[i].trim()));
        i += 1;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    if (isNumberedLine(line)) {
      const items = [];
      while (i < lines.length && isNumberedLine(lines[i].trim())) {
        items.push(stripListMarker(lines[i].trim()));
        i += 1;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    const paragraphLines = [];
    while (i < lines.length) {
      const current = lines[i].trim();

      if (!current) break;
      if (isBulletLine(current) || isNumberedLine(current)) break;

      paragraphLines.push(lines[i].trim());
      i += 1;
    }

    if (paragraphLines.length > 0) {
      blocks.push({
        type: "p",
        text: paragraphLines.join(" "),
      });
    }
  }

  return blocks;
}

// Renders a single message's text with light formatting (paragraphs,
// bullet lists, numbered lists). The `mine` prop toggles styling for
// the user's own messages vs. someone else's.
function FormattedMessageText({ text, mine = false }) {
  const blocks = useMemo(() => parseMessageBlocks(text), [text]);

  const textClass = mine ? "text-white/95" : "text-black/80";
  const subtleTextClass = mine ? "text-white/90" : "text-black/75";

  if (!blocks.length) {
    return <div className="break-words">{text}</div>;
  }

  return (
    <div className="break-words space-y-2">
      {blocks.map((block, idx) => {
        if (block.type === "p") {
          return (
            <p
              key={idx}
              className={`whitespace-pre-wrap leading-relaxed ${textClass}`}
            >
              {block.text}
            </p>
          );
        }

        if (block.type === "ul") {
          return (
            <ul
              key={idx}
              className={`space-y-1 pl-5 list-disc ${subtleTextClass}`}
            >
              {block.items.map((item, itemIdx) => (
                <li key={itemIdx} className="leading-relaxed">
                  <span className={textClass}>{item}</span>
                </li>
              ))}
            </ul>
          );
        }

        if (block.type === "ol") {
          return (
            <ol
              key={idx}
              className={`space-y-1 pl-5 list-decimal ${subtleTextClass}`}
            >
              {block.items.map((item, itemIdx) => (
                <li key={itemIdx} className="leading-relaxed">
                  <span className={textClass}>{item}</span>
                </li>
              ))}
            </ol>
          );
        }

        return (
          <p
            key={idx}
            className={`whitespace-pre-wrap leading-relaxed ${textClass}`}
          >
            {String(block.text || "")}
          </p>
        );
      })}
    </div>
  );
}

export default function Messages() {
  const navigate = useNavigate();
  const location = useLocation();

  // The currently signed-in Firebase user + loading flag.
  const [fbUser, setFbUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");

  // Is the signed-in user staff/admin? Drives a bunch of admin-only
  // UI (Add Admin, Close/Reopen, seeing every thread instead of just
  // their own).
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminName, setAdminName] = useState("Staff member");
  // First name of the signed-in user. Used in message bubbles
  // so "mine" messages can label themselves with the sender's
  // first name instead of a generic role word.
  const [myFirstName, setMyFirstName] = useState("");
  // Staff-only preference (from the user doc). When true and the
  // signed-in user is staff, outgoing messages get a snapshot of the
  // sender's first name so regular users see it under the bubble
  // instead of the generic "Staff" label. Toggled in settings.
  const [myDisplayFirstName, setMyDisplayFirstName] = useState(false);
  // Per-chat override of the preference above. Shape: { [threadId]: bool }.
  // If a given threadId is missing from this map, the global setting
  // (myDisplayFirstName) is used. If present, the override wins for
  // that specific thread. Stored on the user's own doc.
  const [threadFirstNamePrefs, setThreadFirstNamePrefs] = useState({});
  // Disables the per-chat toggle button while a write is in flight, so
  // rapid double-clicks don't fire overlapping Firestore writes.
  const [threadFirstNameSaving, setThreadFirstNameSaving] = useState(false);

  // The list of threads in the left sidebar + which one is open.
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);

  // The active thread's messages + what the user is currently typing.
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");

  // On phones we show either the thread list OR the chat panel,
  // not both at once. This flag controls which one.
  const [showThreads, setShowThreads] = useState(true);

  // "Add admin to thread" feature (admin only). The values below
  // drive the popup that lets an admin invite another admin in.
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [adminDirectoryLoading, setAdminDirectoryLoading] = useState(false);
  const [adminDirectory, setAdminDirectory] = useState([]);
  const [adminSearch, setAdminSearch] = useState("");
  const [selectedAdminUid, setSelectedAdminUid] = useState("");
  const [addAdminBusy, setAddAdminBusy] = useState(false);

  // Used to auto-scroll to the bottom when new messages arrive.
  const bottomRef = useRef(null);

  // Toggles the "Participants" panel inside the active thread header.
  // `participantDirectory` caches { uid -> { name, email, isStaff } }
  // so the panel shows real names instead of raw uids.
  // `loadedParticipantsRef` tracks which uids we've already fetched
  // so the lookup effect doesn't refetch on every threads change.
  const [showParticipants, setShowParticipants] = useState(false);
  const [participantDirectory, setParticipantDirectory] = useState({});
  const loadedParticipantsRef = useRef(new Set());

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      if (!u) {
        navigate("/", { replace: true });
        return;
      }

      setFbUser(u);
      setEmail(u.email || "");
    });

    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    async function run() {
      if (!fbUser?.uid) return;

      try {
        const userRef = doc(db, "users", fbUser.uid);
        const snap = await getDoc(userRef);

        if (!snap.exists()) {
          navigate("/sign-up", { replace: true });
          return;
        }

        const data = snap.data() || {};
        if (!isMounted) return;

        setIsAdmin(data.staff === true);

        const fn = data.firstName || (data.name ? data.name.split(" ")[0] : "");
        const ln =
          data.lastName ||
          (data.name && data.name.split(" ").length > 1
            ? data.name.split(" ").slice(1).join(" ")
            : "");

        const full = `${fn || ""}${ln ? ` ${ln}` : ""}`.trim();
        setAdminName(full || fn || "Staff member");
        setMyFirstName(fn || "");
        setMyDisplayFirstName(data.displayFirstNameInMessages === true);
        setThreadFirstNamePrefs(
          data.threadFirstNamePrefs && typeof data.threadFirstNamePrefs === "object"
            ? data.threadFirstNamePrefs
            : {}
        );
      } catch (e) {
        console.error("Messages: failed to load user doc", e);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    run();

    return () => {
      isMounted = false;
    };
  }, [fbUser?.uid, navigate]);

  useEffect(() => {
    const incoming = location?.state?.threadId;
    if (incoming) {
      setActiveThreadId(incoming);
      setShowThreads(false);
    }
  }, [location?.state]);

  useEffect(() => {
    if (!fbUser?.uid) return;

    setThreadsLoading(true);

    const threadsRef = collection(db, "threads");
    const q = isAdmin
      ? query(threadsRef, orderBy("updatedAt", "desc"), limit(60))
      : query(
          threadsRef,
          where("participants", "array-contains", fbUser.uid),
          limit(60)
        );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

        list.sort((a, b) => {
          const ta = a.updatedAt?.toMillis?.() ?? 0;
          const tb = b.updatedAt?.toMillis?.() ?? 0;
          return tb - ta;
        });

        setThreads(list);
        setThreadsLoading(false);

        if (!activeThreadId && list.length) {
          const firstOpen = list.find((t) => (t.status || "open") !== "closed");
          setActiveThreadId((firstOpen || list[0]).id);
        }

        if (activeThreadId && !list.some((t) => t.id === activeThreadId)) {
          setActiveThreadId(list[0]?.id || null);
        }
      },
      (err) => {
        console.error("Threads snapshot error:", err);
        setThreadsLoading(false);
      }
    );

    return () => unsub();
  }, [fbUser?.uid, isAdmin, activeThreadId]);

  useEffect(() => {
    if (!activeThreadId) {
      setMessages([]);
      return;
    }

    const msgsRef = collection(db, "threads", activeThreadId, "messages");
    const q = query(msgsRef, orderBy("createdAt", "asc"), limit(400));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setMessages(list);

        requestAnimationFrame(() =>
          bottomRef.current?.scrollIntoView({ behavior: "smooth" })
        );
      },
      (err) => console.error("Messages snapshot error:", err)
    );

    return () => unsub();
  }, [activeThreadId]);

  const ongoingThreads = useMemo(
    () => threads.filter((t) => (t.status || "open") !== "closed"),
    [threads]
  );

  const closedThreads = useMemo(
    () => threads.filter((t) => (t.status || "open") === "closed"),
    [threads]
  );

  // For each unique participant uid that appears in the loaded
  // threads, look up the user's display name + role so the
  // Participants panel can show "Jane Doe (admin)" instead of a
  // raw uid. We fetch in chunks of 30 because Firestore caps an
  // `in` query at 30 values per call.
  useEffect(() => {
    if (!isAdmin || !fbUser?.uid) return;

    const allUids = new Set();
    threads.forEach((t) => {
      (t.participants || []).forEach((uid) => {
        if (uid && uid !== fbUser.uid) allUids.add(uid);
      });
    });

    const missing = [...allUids].filter(
      (uid) => !loadedParticipantsRef.current.has(uid)
    );
    if (missing.length === 0) return;

    missing.forEach((uid) => loadedParticipantsRef.current.add(uid));

    let cancelled = false;
    (async () => {
      const additions = {};
      const chunkSize = 30;
      for (let i = 0; i < missing.length; i += chunkSize) {
        const chunk = missing.slice(i, i + chunkSize);
        try {
          const snap = await getDocs(
            query(collection(db, "users"), where(documentId(), "in", chunk))
          );
          snap.docs.forEach((d) => {
            const data = d.data() || {};
            const fn =
              data.firstName || (data.name ? data.name.split(" ")[0] : "");
            const ln =
              data.lastName ||
              (data.name && data.name.split(" ").length > 1
                ? data.name.split(" ").slice(1).join(" ")
                : "");
            const full = `${fn || ""}${ln ? ` ${ln}` : ""}`.trim();
            additions[d.id] = {
              name: full || data.name || data.email || "Unknown user",
              firstName: fn || "",
              email: data.email || "",
              isStaff: data.staff === true,
            };
          });
          // Any uid we asked for but didn't get back (deleted user,
          // permission denied, etc.) gets a placeholder so the
          // Participants panel doesn't sit on "Loading…" forever.
          chunk.forEach((uid) => {
            if (!additions[uid]) {
              additions[uid] = {
                name: "Unknown user",
                email: "",
                isStaff: false,
              };
            }
          });
        } catch (e) {
          console.error("Failed to load participant names:", e);
        }
      }
      if (!cancelled && Object.keys(additions).length > 0) {
        setParticipantDirectory((prev) => ({ ...prev, ...additions }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [threads, isAdmin, fbUser?.uid]);

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeThreadId) || null,
    [threads, activeThreadId]
  );

  const canChat = useMemo(() => {
    if (!activeThreadId) return false;
    if ((activeThread?.status || "open") === "closed") return false;
    return true;
  }, [activeThreadId, activeThread?.status]);

  // The actual on/off state for "show my first name" in the
  // currently-open chat. Falls back to the global setting if there's
  // no per-chat override for this thread.
  const effectiveDisplayFirstName = useMemo(() => {
    if (!activeThreadId) return myDisplayFirstName;
    if (
      Object.prototype.hasOwnProperty.call(
        threadFirstNamePrefs,
        activeThreadId
      )
    ) {
      return threadFirstNamePrefs[activeThreadId] === true;
    }
    return myDisplayFirstName;
  }, [activeThreadId, threadFirstNamePrefs, myDisplayFirstName]);

  // The participants of the currently-active thread, decorated
  // with display name + role from participantDirectory. Sorted so
  // "You" comes first, then non-staff users, then admins.
  const activeThreadParticipants = useMemo(() => {
    const uids = activeThread?.participants || [];
    return [...uids]
      .map((uid) => {
        const isMe = uid === fbUser?.uid;
        const info = participantDirectory[uid];
        return {
          uid,
          isMe,
          name: isMe ? "You" : info?.name || "Loading…",
          email: info?.email || "",
          isStaff: isMe ? isAdmin : info?.isStaff === true,
          loaded: isMe || Boolean(info),
        };
      })
      .sort((a, b) => {
        if (a.isMe !== b.isMe) return a.isMe ? -1 : 1;
        if (a.isStaff !== b.isStaff) return a.isStaff ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
  }, [activeThread?.participants, participantDirectory, fbUser?.uid, isAdmin]);

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = useCallback(async () => {
    try {
      setLoggingOut(true);
      await signOut(auth);
      localStorage.removeItem("userEmail");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout Failed:", error);
      alert("Logout failed. Please try again.");
      setLoggingOut(false);
      setLogoutConfirmOpen(false);
    }
  }, [navigate]);

  // Admins can close/reopen a thread. This writes the new status to
  // the thread document so it shows up in the right section
  // (Ongoing vs. Closed) the next time the list reloads.
  async function adminSetThreadStatus(nextStatus) {
    if (!isAdmin || !activeThreadId || !fbUser?.uid) return;

    await updateDoc(doc(db, "threads", activeThreadId), {
      status: nextStatus,
      updatedAt: serverTimestamp(),
    });

    const actionWord = nextStatus === "closed" ? "closed" : "reopened";
    const notice = `${adminName || "Staff member"} has ${actionWord} this thread.`;

    await addDoc(collection(db, "threads", activeThreadId, "messages"), {
      text: notice,
      senderUid: fbUser.uid,
      senderRole: "admin",
      createdAt: serverTimestamp(),
      system: true,
      systemType: nextStatus,
    });

    await updateDoc(doc(db, "threads", activeThreadId), {
      lastMessage: notice,
      lastSenderUid: fbUser.uid,
      updatedAt: serverTimestamp(),
    });
  }

  // Sends the message currently in the typing box. Adds it to the
  // active thread's `messages` sub-collection and updates the thread's
  // `lastMessage` + `updatedAt` so the sidebar preview reflects it.
  async function sendMessage() {
    const text = draft.trim();
    if (!text || !fbUser?.uid) return;

    const threadId = activeThreadId;
    if (!threadId) return;
    if (!canChat) return;

    setDraft("");

    const messageData = {
      text,
      senderUid: fbUser.uid,
      senderRole: isAdmin ? "admin" : "user",
      createdAt: serverTimestamp(),
    };

    // Staff-only opt-in: if this staff has chosen to show their first
    // name in messages, snapshot it onto the message so regular users
    // see "Sarah" under the bubble instead of the generic "Staff".
    // Uses the per-chat override when one is set for this thread,
    // otherwise the global Settings value. Saved at send-time so
    // toggling the preference later doesn't retroactively rewrite past
    // messages.
    if (isAdmin && effectiveDisplayFirstName && myFirstName) {
      messageData.senderFirstName = myFirstName;
    }

    await addDoc(
      collection(db, "threads", threadId, "messages"),
      messageData
    );

    await updateDoc(doc(db, "threads", threadId), {
      lastMessage: text,
      lastSenderUid: fbUser.uid,
      updatedAt: serverTimestamp(),
    });
  }

  // Flips the "show my first name" override for the currently-open
  // chat. Writes to the signed-in staff's own user doc using a Firestore
  // dotted-path update so only this thread's entry inside the
  // threadFirstNamePrefs map is touched (other threads' overrides stay
  // intact). Optimistically updates local state, reverts on failure.
  async function toggleFirstNameInThread() {
    if (!isAdmin || !activeThreadId || !fbUser?.uid) return;
    if (threadFirstNameSaving) return;

    const next = !effectiveDisplayFirstName;
    const prevSnapshot = threadFirstNamePrefs;

    setThreadFirstNamePrefs((prev) => ({ ...prev, [activeThreadId]: next }));
    setThreadFirstNameSaving(true);

    try {
      await updateDoc(doc(db, "users", fbUser.uid), {
        [`threadFirstNamePrefs.${activeThreadId}`]: next,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("Failed to save per-chat first name preference:", e);
      setThreadFirstNamePrefs(prevSnapshot);
      alert(
        "Could not save first name preference for this chat. Please try again."
      );
    } finally {
      setThreadFirstNameSaving(false);
    }
  }

  // Loads the list of admin users (staff = true) so an admin can
  // pick one to add to the current thread. Called when the "Add Admin"
  // popup is opened or refreshed.
  const loadAdminDirectory = useCallback(async () => {
    if (!isAdmin) return;

    setAdminDirectoryLoading(true);

    try {
      const usersRef = collection(db, "users");

      const staffSnap = await getDocs(
        query(usersRef, where("staff", "==", true), limit(200))
      );

      const merged = new Map();

      staffSnap.docs.forEach((d) => {
        const data = d.data() || {};
        const fn = data.firstName || (data.name ? data.name.split(" ")[0] : "");
        const ln =
          data.lastName ||
          (data.name && data.name.split(" ").length > 1
            ? data.name.split(" ").slice(1).join(" ")
            : "");

        const full = `${fn || ""}${ln ? ` ${ln}` : ""}`.trim();

        if (d.id !== fbUser?.uid) {
          merged.set(d.id, {
            uid: d.id,
            name: full || data.name || fn || "Staff member",
            email: data.email || "",
          });
        }
      });

      const list = Array.from(merged.values());

      list.sort((a, b) => {
        const an = (a.name || "").toLowerCase();
        const bn = (b.name || "").toLowerCase();

        if (an < bn) return -1;
        if (an > bn) return 1;

        const ae = (a.email || "").toLowerCase();
        const be = (b.email || "").toLowerCase();

        return ae.localeCompare(be);
      });

      setAdminDirectory(list);
    } catch (e) {
      console.error("Failed to load admin directory:", e);
    } finally {
      setAdminDirectoryLoading(false);
    }
  }, [isAdmin, fbUser?.uid]);

  useEffect(() => {
    setShowAddAdmin(false);
    setAdminSearch("");
    setSelectedAdminUid("");
    setShowParticipants(false);
  }, [activeThreadId]);

  const eligibleAdmins = useMemo(() => {
    const participants = activeThread?.participants || [];
    const term = adminSearch.trim().toLowerCase();

    const base = adminDirectory.filter((u) => !participants.includes(u.uid));

    if (!term) return base.slice(0, 20);

    const filtered = base.filter((u) => {
      const n = (u.name || "").toLowerCase();
      const e = (u.email || "").toLowerCase();
      return n.includes(term) || e.includes(term);
    });

    return filtered.slice(0, 20);
  }, [adminDirectory, activeThread?.participants, adminSearch]);

  const selectedAdmin = useMemo(() => {
    if (!selectedAdminUid) return null;
    return adminDirectory.find((u) => u.uid === selectedAdminUid) || null;
  }, [adminDirectory, selectedAdminUid]);

  // Adds the admin selected in the "Add Admin" popup to the current
  // thread's `participants` array (so they can see and reply to it).
  async function addAdminToThread() {
    if (!isAdmin || !activeThreadId || !fbUser?.uid) return;
    if (!selectedAdminUid) return;

    setAddAdminBusy(true);

    try {
      const threadRef = doc(db, "threads", activeThreadId);

      await updateDoc(threadRef, {
        participants: arrayUnion(selectedAdminUid),
        updatedAt: serverTimestamp(),
      });

      const addedLabel = selectedAdmin?.name || selectedAdmin?.email || "another staff member";
      const notice = `${adminName || "Staff member"} added ${addedLabel} to this thread.`;

      await addDoc(collection(db, "threads", activeThreadId, "messages"), {
        text: notice,
        senderUid: fbUser.uid,
        senderRole: "admin",
        createdAt: serverTimestamp(),
        system: true,
        systemType: "add_admin",
      });

      await updateDoc(threadRef, {
        lastMessage: notice,
        lastSenderUid: fbUser.uid,
        updatedAt: serverTimestamp(),
      });

      setAdminSearch("");
      setSelectedAdminUid("");
      setShowAddAdmin(false);
    } catch (e) {
      console.error("Failed to add admin to thread:", e);
      alert("Could not add staff member to this thread. Check console + Firestore rules.");
    } finally {
      setAddAdminBusy(false);
    }
  }

  if (loading) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        style={pageBackground}
      >
        <div
          className="rounded-2xl border px-5 py-4 bg-white shadow-lg"
          style={{ borderColor: THEME.border }}
        >
          <div className="text-base font-extrabold" style={{ color: THEME.text }}>
            Loading…
          </div>
        </div>
      </div>
    );
  }

  const activeStatus = (activeThread?.status || "open") === "closed" ? "Closed" : "Open";

  return (
    <div className="flex-1 relative flex flex-col min-h-0" style={pageBackground}>
      {/* --- Page header: logo on left, Home + Logout on right --- */}
      <header
        className="sticky top-0 z-20 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 transition"
            aria-label="Go to home"
            type="button"
          >
            <img src={logo} alt="Logo" className="w-13 h-12 object-contain" />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowLight }}
              >
                Messages
              </div>
            </div>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/home")}
              className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
              type="button"
            >
              Home
            </button>

            <button
              onClick={() => setLogoutConfirmOpen(true)}
              className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
              type="button"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 flex flex-col w-full max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <div
          className="mb-6 rounded-3xl border shadow-sm backdrop-blur px-5 sm:px-6 py-5"
          style={{
            backgroundColor: THEME.cardBg,
            borderColor: THEME.border,
            boxShadow: THEME.shadow,
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 flex-wrap">
                <span
                  className="text-[12px] font-extrabold px-2.5 py-1 rounded-full"
                  style={{
                    color: COLORS.brown,
                  }}
                >
                  {isAdmin ? "Staff Inbox" : "Support Chat"}
                </span>

                {activeThreadId ? (
                  <span
                    className="text-[12px] font-extrabold px-2.5 py-1 rounded-full border"
                    style={{
                      color:
                        activeStatus === "Closed"
                          ? COLORS.brown
                          : COLORS.aquaDeep,
                      backgroundColor:
                        activeStatus === "Closed"
                          ? COLORS.yellowLight
                          : COLORS.aquaLight,
                      borderColor:
                        activeStatus === "Closed"
                          ? "rgba(61,51,0,0.18)"
                          : "rgba(0,96,110,0.18)",
                    }}
                  >
                    {activeStatus}
                  </span>
                ) : null}
              </div>

              <h1
                className="text-3xl sm:text-4xl font-extrabold mt-3"
                style={{ color: THEME.text }}
              >
                Messages
              </h1>

              <p
                className="mt-2 max-w-2xl"
                style={{ color: "rgba(31,56,0,0.66)" }}
              >
                {isAdmin
                  ? "View and reply to user threads."
                  : "You’ll see message threads here once a staff member adds you."}
              </p>
            </div>

            <div className="md:hidden flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowThreads(true)}
                type="button"
                className="px-3 py-2 rounded-xl border text-sm font-extrabold transition backdrop-blur"
                style={{
                  color: THEME.text,
                  backgroundColor: showThreads
                    ? COLORS.greenLight
                    : "rgba(255,255,255,0.74)",
                  borderColor: THEME.border,
                  boxShadow: showThreads
                    ? "0 8px 18px rgba(31,56,0,0.1)"
                    : "none",
                }}
              >
                Threads
              </button>

              <button
                onClick={() => setShowThreads(false)}
                type="button"
                className="px-3 py-2 rounded-xl border text-sm font-extrabold transition backdrop-blur"
                style={{
                  color: THEME.text,
                  backgroundColor: !showThreads
                    ? COLORS.greenLight
                    : "rgba(255,255,255,0.74)",
                  borderColor: THEME.border,
                  boxShadow: !showThreads
                    ? "0 8px 18px rgba(31,56,0,0.1)"
                    : "none",
                }}
              >
                Chat
              </button>
            </div>
          </div>
        </div>

        {/* --- Two-column layout: thread list on the left, chat
                panel on the right. On phones, only one shows at a
                time (toggled by `showThreads`). --- */}
        <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-6 flex-1 min-h-0">
          {/* Left column: thread sidebar (Ongoing + Closed lists). */}
          <section className={showThreads ? "block" : "hidden md:block"}>
            <div
              className="rounded-2xl border p-4 sm:p-5 lg:p-6 backdrop-blur"
              style={{
                backgroundColor: THEME.cardBg,
                borderColor: THEME.border,
                boxShadow: "0 14px 30px rgba(31,56,0,0.1)",
              }}
            >
              <div
                className="text-sm font-extrabold"
                style={{ color: THEME.text }}
              >
                Ongoing Threads
              </div>

              <div className="mt-3 space-y-2 max-h-[45vh] overflow-y-auto pr-1" style={{ scrollbarGutter: "stable" }}>
                {threadsLoading ? (
                  <div
                    className="text-sm"
                    style={{ color: "rgba(31,56,0,0.62)" }}
                  >
                    Loading threads…
                  </div>
                ) : ongoingThreads.length === 0 ? (
                  <div
                    className="rounded-2xl border border-dashed p-4 backdrop-blur"
                    style={{
                      backgroundColor: THEME.cardBgSoft,
                      borderColor: THEME.border,
                    }}
                  >
                    <div
                      className="text-sm font-extrabold"
                      style={{ color: THEME.text }}
                    >
                      No ongoing threads
                    </div>
                    <div
                      className="text-sm mt-1"
                      style={{ color: "rgba(31,56,0,0.62)" }}
                    >
                      {isAdmin
                        ? "Waiting for incoming messages."
                        : "A staff member will add you to a thread if needed."}
                    </div>
                  </div>
                ) : (
                  ongoingThreads.map((t) => (
                    <ThreadRow
                      key={t.id}
                      thread={t}
                      active={t.id === activeThreadId}
                      onClick={() => {
                        setActiveThreadId(t.id);
                        setShowThreads(false);
                      }}
                    />
                  ))
                )}
              </div>
            </div>

            <div
              className="mt-4 rounded-2xl border p-4 sm:p-5 lg:p-6 backdrop-blur"
              style={{
                backgroundColor: THEME.cardBgSoft,
                borderColor: THEME.border,
                boxShadow: "0 14px 30px rgba(31,56,0,0.08)",
              }}
            >
              <div
                className="text-sm font-extrabold"
                style={{ color: THEME.text }}
              >
                Closed
              </div>

              <div className="mt-3 space-y-2 max-h-[35vh] overflow-y-auto pr-1" style={{ scrollbarGutter: "stable" }}>
                {threadsLoading ? (
                  <div
                    className="text-sm"
                    style={{ color: "rgba(31,56,0,0.62)" }}
                  >
                    Loading…
                  </div>
                ) : closedThreads.length === 0 ? (
                  <div
                    className="text-sm"
                    style={{ color: "rgba(31,56,0,0.62)" }}
                  >
                    No closed threads.
                  </div>
                ) : (
                  closedThreads.map((t) => (
                    <ThreadRow
                      key={t.id}
                      thread={t}
                      active={t.id === activeThreadId}
                      onClick={() => {
                        setActiveThreadId(t.id);
                        setShowThreads(false);
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          </section>

          {/* Right column: the chat panel for the active thread.
              Contains the thread header (with admin Close/Add Admin
              controls), the scrolling list of messages, and the
              composer (typing box). */}
          <section className={`${!showThreads ? "flex" : "hidden md:flex"} flex-col min-h-0`}>
            <div
              className="rounded-3xl border overflow-hidden backdrop-blur flex-1 flex flex-col min-h-0"
              style={{
                backgroundColor: THEME.cardBg,
                borderColor: THEME.border,
                boxShadow: THEME.shadow,
              }}
            >
              <div
                className="px-6 py-4 border-b backdrop-blur"
                style={{
                  backgroundColor: "rgba(255,255,255,0.72)",
                  borderColor: THEME.border,
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div
                      className="text-base font-extrabold truncate"
                      style={{ color: THEME.text }}
                    >
                      {activeThread?.title ||
                        (activeThreadId ? "Message Thread" : "No thread selected")}
                    </div>

                    <div
                      className="text-sm mt-1"
                      style={{ color: "rgba(31,56,0,0.62)" }}
                    >
                      {activeThreadId
                        ? (activeThread?.status || "open") === "closed"
                          ? "This thread is closed."
                          : isAdmin
                          ? "Reply as staff."
                          : `Signed in as ${email || "user"}`
                        : isAdmin
                        ? "Select a thread on the left."
                        : "No threads yet — you’ll see messages once you’re added to a thread."}
                    </div>
                  </div>

                  {isAdmin && activeThreadId ? (
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <button
                        onClick={() => setShowParticipants((v) => !v)}
                        className="text-[12px] font-extrabold rounded-xl border px-3 py-2 transition hover:opacity-95"
                        style={{
                          backgroundColor: showParticipants
                            ? COLORS.greenLight
                            : "rgba(255,255,255,0.74)",
                          color: THEME.text,
                          borderColor: THEME.border,
                        }}
                        type="button"
                        aria-expanded={showParticipants}
                      >
                        Participants ({activeThreadParticipants.length})
                      </button>

                      <button
                        onClick={toggleFirstNameInThread}
                        disabled={threadFirstNameSaving}
                        className="text-[12px] font-extrabold rounded-xl border px-3 py-2 transition hover:opacity-95 disabled:opacity-50"
                        style={{
                          backgroundColor: effectiveDisplayFirstName
                            ? COLORS.greenLight
                            : "rgba(255,255,255,0.74)",
                          color: THEME.text,
                          borderColor: THEME.border,
                        }}
                        type="button"
                        aria-pressed={effectiveDisplayFirstName}
                        title="Toggle whether your first name shows on messages you send in this chat. Overrides the global setting from Settings."
                      >
                        My Name: {effectiveDisplayFirstName ? "On" : "Off"}
                      </button>

                      <button
                        onClick={async () => {
                          const next = !showAddAdmin;
                          setShowAddAdmin(next);
                          if (next && adminDirectory.length === 0) {
                            await loadAdminDirectory();
                          }
                        }}
                        className="text-[12px] font-extrabold rounded-xl border px-3 py-2 text-white shadow-sm hover:opacity-95 transition"
                        style={{
                          backgroundColor: COLORS.aquaDark,
                          borderColor: "rgba(0,56,64,0.18)",
                        }}
                        type="button"
                      >
                        Add Staff
                      </button>

                      {(activeThread?.status || "open") === "closed" ? (
                        <button
                          onClick={() => adminSetThreadStatus("open")}
                          className="text-[12px] font-extrabold rounded-xl border px-3 py-2 text-white shadow-sm hover:opacity-95 transition"
                          style={{
                            backgroundColor: THEME.headerBg,
                            borderColor: "rgba(31,56,0,0.18)",
                          }}
                          type="button"
                        >
                          Reopen
                        </button>
                      ) : (
                        <button
                          onClick={() => adminSetThreadStatus("closed")}
                          className="text-[12px] font-extrabold rounded-xl border px-3 py-2 text-white shadow-sm hover:opacity-95 transition"
                          style={{
                            backgroundColor: THEME.headerBg,
                            borderColor: "rgba(31,56,0,0.18)",
                          }}
                          type="button"
                        >
                          Close
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>

                {isAdmin && activeThreadId && showParticipants ? (
                  <div
                    className="mt-3 rounded-2xl border p-3"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.82)",
                      borderColor: THEME.border,
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div
                        className="text-[12px] font-extrabold"
                        style={{ color: THEME.text }}
                      >
                        Thread Participants ({activeThreadParticipants.length})
                      </div>

                      <button
                        onClick={() => setShowParticipants(false)}
                        className="text-[12px] font-extrabold"
                        style={{ color: "rgba(31,56,0,0.58)" }}
                        type="button"
                      >
                        Close
                      </button>
                    </div>

                    <ul
                      className="mt-2 max-h-56 overflow-y-auto rounded-xl border bg-white divide-y"
                      style={{ borderColor: THEME.border }}
                    >
                      {activeThreadParticipants.map((p) => (
                        <li
                          key={p.uid}
                          className="px-3 py-2 flex items-center justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <div
                              className="text-sm font-extrabold truncate"
                              style={{ color: THEME.text }}
                            >
                              {p.name}
                            </div>
                            {p.email && !p.isMe ? (
                              <div
                                className="text-[12px] truncate"
                                style={{ color: "rgba(31,56,0,0.62)" }}
                              >
                                {p.email}
                              </div>
                            ) : null}
                          </div>

                          {p.loaded ? (
                            <span
                              className="shrink-0 text-[11px] font-extrabold px-2 py-0.5 rounded-full border"
                              style={{
                                color: p.isStaff ? COLORS.aquaDeep : THEME.text,
                                backgroundColor: p.isStaff
                                  ? COLORS.aquaLight
                                  : COLORS.greenLight,
                                borderColor: THEME.border,
                              }}
                            >
                              {p.isStaff ? "Staff" : "User"}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {isAdmin && activeThreadId && showAddAdmin ? (
                  <div
                    className="mt-3 rounded-2xl border p-3"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.82)",
                      borderColor: THEME.border,
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div
                        className="text-[12px] font-extrabold"
                        style={{ color: THEME.text }}
                      >
                        Add another staff member to this thread
                      </div>

                      <button
                        onClick={() => {
                          setShowAddAdmin(false);
                          setAdminSearch("");
                          setSelectedAdminUid("");
                        }}
                        className="text-[12px] font-extrabold"
                        style={{ color: "rgba(31,56,0,0.58)" }}
                        type="button"
                      >
                        Close
                      </button>
                    </div>

                    <div className="mt-2 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
                      <div className="relative">
                        <input
                          value={adminSearch}
                          onChange={(e) => {
                            setAdminSearch(e.target.value);
                            setSelectedAdminUid("");
                          }}
                          placeholder={
                            adminDirectoryLoading
                              ? "Loading staff…"
                              : "Search staff by name or email…"
                          }
                          disabled={adminDirectoryLoading}
                          className="w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black/10"
                          style={{
                            borderColor: THEME.border,
                            color: THEME.text,
                          }}
                        />

                        <div
                          className="mt-2 max-h-56 overflow-auto rounded-xl border bg-white"
                          style={{ borderColor: THEME.border }}
                        >
                          {adminDirectoryLoading ? (
                            <div
                              className="px-3 py-2 text-sm"
                              style={{ color: "rgba(31,56,0,0.62)" }}
                            >
                              Loading…
                            </div>
                          ) : eligibleAdmins.length === 0 ? (
                            <div
                              className="px-3 py-2 text-sm"
                              style={{ color: "rgba(31,56,0,0.62)" }}
                            >
                              No matching staff, or they’re already in the thread.
                            </div>
                          ) : (
                            eligibleAdmins.map((u) => {
                              const active = u.uid === selectedAdminUid;

                              return (
                                <button
                                  key={u.uid}
                                  type="button"
                                  onClick={() => setSelectedAdminUid(u.uid)}
                                  className="w-full text-left px-3 py-2 text-sm transition"
                                  style={{
                                    backgroundColor: active
                                      ? COLORS.greenLight
                                      : "transparent",
                                  }}
                                >
                                  <div
                                    className="font-extrabold truncate"
                                    style={{ color: THEME.text }}
                                  >
                                    {u.name || "Staff member"}
                                  </div>
                                  <div
                                    className="text-[12px] truncate"
                                    style={{ color: "rgba(31,56,0,0.52)" }}
                                  >
                                    {u.email || u.uid}
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>

                      <div className="flex items-stretch gap-2">
                        <button
                          onClick={loadAdminDirectory}
                          disabled={adminDirectoryLoading}
                          className="rounded-xl border bg-white px-3 py-2 text-sm font-extrabold transition disabled:opacity-50"
                          style={{
                            color: "rgba(31,56,0,0.68)",
                            borderColor: THEME.border,
                          }}
                          type="button"
                        >
                          Refresh
                        </button>

                        <button
                          onClick={addAdminToThread}
                          disabled={!selectedAdminUid || addAdminBusy}
                          className="rounded-xl px-4 py-2 text-sm font-extrabold text-white disabled:opacity-40 transition shadow-sm hover:opacity-95"
                          style={{ backgroundColor: THEME.headerBg }}
                          type="button"
                        >
                          {addAdminBusy ? "Adding…" : "Add"}
                        </button>
                      </div>
                    </div>

                    {selectedAdmin ? (
                      <div
                        className="mt-2 text-[12px]"
                        style={{ color: "rgba(31,56,0,0.58)" }}
                      >
                        Selected:{" "}
                        <span
                          className="font-extrabold"
                          style={{ color: THEME.text }}
                        >
                          {selectedAdmin.name}
                        </span>{" "}
                        <span style={{ color: "rgba(31,56,0,0.45)" }}>
                          {selectedAdmin.email ? `(${selectedAdmin.email})` : ""}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div
                className="flex-1 min-h-0 overflow-y-auto px-4 py-4"
                style={{ backgroundColor: "rgba(255,255,255,0.54)" }}
              >
                {!activeThreadId ? (
                  <div
                    className="text-sm"
                    style={{ color: "rgba(31,56,0,0.62)" }}
                  >
                    {isAdmin
                      ? "Pick a thread to view messages."
                      : "No thread yet — you’ll see messages once you’re added to a thread."}
                  </div>
                ) : messages.length === 0 ? (
                  <div
                    className="text-sm"
                    style={{ color: "rgba(31,56,0,0.62)" }}
                  >
                    No messages yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {messages.map((m) => {
                      const mine = m.senderUid === fbUser?.uid;

                      // What to show under the bubble: sender's first
                      // name when we know it, falling back to "You" for
                      // own messages and the role for everyone else.
                      // For someone else's messages we prefer the
                      // `senderFirstName` snapshotted on the message
                      // (set when a staff member opts in via Settings),
                      // then the participant directory (admin viewers
                      // only), then the role-based fallback.
                      const senderFirstName = mine
                        ? myFirstName
                        : m.senderFirstName ||
                          participantDirectory[m.senderUid]?.firstName ||
                          "";
                      const senderLabel =
                        senderFirstName ||
                        (mine
                          ? "You"
                          : m.senderRole === "admin"
                          ? "Staff"
                          : "User");

                      if (m.system) {
                        return (
                          <div key={m.id} className="flex justify-center">
                            <div
                              className="max-w-[92%] rounded-2xl px-4 py-3 text-[13px] font-semibold shadow-sm border backdrop-blur"
                              style={{
                                backgroundColor: THEME.headerBg,
                                color: "white",
                                borderColor: "rgba(231,247,210,0.22)",
                              }}
                            >
                              <FormattedMessageText text={m.text} mine />
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={m.id}
                          className={mine ? "flex justify-end" : "flex justify-start"}
                        >
                          <div
                            className={[
                              "max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm backdrop-blur border",
                              mine ? "text-white" : "bg-white/90 text-black/80",
                            ].join(" ")}
                            style={{
                              backgroundColor: mine ? COLORS.aquaDark : undefined,
                              borderColor: mine
                                ? "rgba(0,56,64,0.18)"
                                : THEME.border,
                            }}
                          >
                            <FormattedMessageText text={m.text} mine={mine} />

                            <div
                              className={
                                mine
                                  ? "mt-2 text-[11px] text-white/80"
                                  : "mt-2 text-[11px]"
                              }
                              style={
                                mine
                                  ? undefined
                                  : { color: "rgba(31,56,0,0.48)" }
                              }
                            >
                              {senderLabel}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div ref={bottomRef} />
                  </div>
                )}
              </div>

              <div
                className="px-4 py-4 border-t backdrop-blur"
                style={{
                  backgroundColor: "rgba(255,255,255,0.76)",
                  borderColor: THEME.border,
                }}
              >
                <div className="flex items-end gap-2">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (!canChat) return;

                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    rows={2}
                    placeholder={
                      !activeThreadId
                        ? isAdmin
                          ? "Select a thread to reply…"
                          : "No thread selected…"
                        : !canChat
                        ? "This thread is closed."
                        : "Type a message…"
                    }
                    disabled={!canChat}
                    className="flex-1 resize-none rounded-2xl border bg-white/90 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-black/10 disabled:bg-black/5"
                    style={{
                      borderColor: THEME.border,
                      color: THEME.text,
                    }}
                  />

                  <button
                    onClick={sendMessage}
                    disabled={!draft.trim() || !canChat}
                    className="rounded-2xl px-5 py-3 text-sm font-extrabold text-white disabled:opacity-40 transition shadow-sm hover:opacity-95"
                    style={{ backgroundColor: THEME.headerBg }}
                    type="button"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>

            <div className="md:hidden mt-4">
              <button
                onClick={() => setShowThreads(true)}
                className="w-full rounded-2xl border backdrop-blur px-4 py-3 text-sm font-extrabold hover:bg-white/90 transition shadow-sm"
                style={{
                  color: THEME.text,
                  backgroundColor: "rgba(255,255,255,0.88)",
                  borderColor: THEME.border,
                }}
                type="button"
              >
                Back to Threads
              </button>
            </div>
          </section>
        </div>
      </main>

      <LogoutConfirm
        open={logoutConfirmOpen}
        busy={loggingOut}
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}